/**
 * Componente para seleção/busca de clientes
 */

import React from 'react';
import { Search, Check, X, User } from 'lucide-react';
import { Cliente } from '../../../../types/gerArquivos';

interface ClienteSelectorProps {
  query: string;
  onQueryChange: (q: string) => void;
  cliente: Cliente | null;
  clientes: Cliente[];
  carregando: boolean;
  matchStatus: 'found' | 'not_found' | null;
  isConfirmed: boolean;
  resumo: string;
  isTypingWhatsApp?: boolean;
  onSelectCliente: (c: Cliente) => void;
  onConfirmCliente: () => void;
  onClearCliente: () => void;
}

export const ClienteSelector: React.FC<ClienteSelectorProps> = ({
  query,
  onQueryChange,
  cliente,
  clientes,
  carregando,
  matchStatus,
  isConfirmed,
  resumo,
  isTypingWhatsApp = false,
  onSelectCliente,
  onConfirmCliente,
  onClearCliente
}) => {
  return (
    <div className="space-y-2">
      <label className="block text-xs text-gray-300 font-medium">
        Cliente ou WhatsApp <span className="text-red-400">*</span>
      </label>

      {/* Input de busca */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input
          type="text"
          placeholder={isConfirmed ? "Cliente selecionado" : "Digite o nome do cliente ou o WhatsApp"}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          disabled={isConfirmed}
          className="w-full pl-8 pr-8 py-1.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400/50 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {carregando && !isConfirmed && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {matchStatus === 'found' && !isConfirmed && (
          <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-green-400" />
        )}
        {matchStatus === 'not_found' && !isConfirmed && (
          <X className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-red-400" />
        )}
        {isConfirmed && (
          <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-blue-400" />
        )}
      </div>

      {/* Cliente encontrado por WhatsApp - aguardando confirmação */}
      {matchStatus === 'found' && cliente && !isConfirmed && (
        <div className="p-2 bg-green-600/10 border border-green-600/20 rounded-lg">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0 text-xs text-green-100">
              <p className="text-green-300 font-medium flex items-center gap-1 mb-1">
                <User className="w-3 h-3" />
                Cliente encontrado
              </p>
              <p className="text-[10px] leading-relaxed text-green-100 break-words">
                {resumo}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <button
                type="button"
                onClick={onConfirmCliente}
                className="px-2 py-1 text-[10px] rounded border border-green-500 text-green-200 hover:bg-green-500/20 transition-colors font-medium"
              >
                Selecionar
              </button>
              <button
                type="button"
                onClick={onClearCliente}
                className="text-[10px] text-gray-400 hover:text-gray-200 transition-colors"
              >
                Limpar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cliente selecionado e confirmado */}
      {isConfirmed && cliente && (
        <div className="p-2 bg-blue-600/10 border border-blue-600/20 rounded-lg">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0 text-xs text-blue-100">
              <p className="text-blue-300 font-medium flex items-center gap-1 mb-1">
                <Check className="w-3 h-3" />
                Cliente confirmado
              </p>
              <p className="text-[10px] leading-relaxed text-blue-100 break-words">
                {resumo}
              </p>
            </div>
            <button
              type="button"
              onClick={onClearCliente}
              className="text-[10px] text-gray-400 hover:text-gray-200 transition-colors px-2 py-1 hover:bg-navy-700 rounded"
            >
              Alterar
            </button>
          </div>
        </div>
      )}

      {/* Lista de clientes (busca por nome) */}
      {!matchStatus && clientes.length > 0 && !isConfirmed && (
        <div className="max-h-40 overflow-y-auto bg-navy-800 border border-navy-600 rounded-lg divide-y divide-navy-700">
          {clientes.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelectCliente(c)}
              className="w-full px-2 py-2 text-left hover:bg-navy-700 transition-colors"
            >
              <div className="text-xs text-gray-100 font-medium truncate">{c.empresa || c.nome}</div>
              {c.whatsapp && (
                <div className="text-[10px] text-gray-400 mt-0.5 truncate">WhatsApp: {c.whatsapp}</div>
              )}
              {c.cnpjCpf && (
                <div className="text-[10px] text-gray-400 truncate">{c.cnpjCpf}</div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* WhatsApp não encontrado */}
      {matchStatus === 'not_found' && !isConfirmed && (
        <div className="p-2 bg-yellow-600/10 border border-yellow-600/20 rounded-lg">
          <p className="text-[10px] text-yellow-200">
            WhatsApp não cadastrado. O arquivo será salvo como <strong>SEM-CADASTRO</strong>.
          </p>
        </div>
      )}

      {/* Feedback: WhatsApp inválido enquanto digita */}
      {isTypingWhatsApp && !matchStatus && !isConfirmed && (
        <div className="p-2 bg-red-600/10 border border-red-600/20 rounded-lg">
          <p className="text-[10px] text-red-200">
            <strong>WhatsApp inválido.</strong> Deve ter 10 ou 11 dígitos válidos (DDD + número). Exemplo: <strong>6291450653</strong> ou <strong>629991450653</strong>
          </p>
        </div>
      )}
    </div>
  );
};

